"use client";

import React from "react";
import Image from "next/image";
import {
  Calendar,
  Clock,
  FileText,
  Heart,
  Shield,
  Phone,
  MapPin,
} from "lucide-react";

const NewPatientsPage = () => {
  const handleBookingClick = () => {
    window.open('https://app.nexhealth.com/appt/smile4me_dental_care?lid=77847&tracking_key=b2d71902-5ce0-43c1-bc95-24cdee27de9a', '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative h-[70vh] bg-gradient-to-br from-gray-900 to-gray-700 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <Image
              src="/images/custom_images_office_10.webp"
              alt="Smile4Me dental office"
              fill
              className="object-cover opacity-60"
              sizes="100vw"
              quality={80}
              priority
            />
          </div>

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />

          {/* Content */}
          <div className="relative z-10 flex items-center justify-center h-full px-4 sm:px-8">
            <div className="text-center text-white max-w-4xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Astoria Dentist Welcomes New Patients
              </h1>
              <p className="text-xl md:text-2xl mb-8 opacity-90">
                Your journey to optimal oral health starts here
              </p>
              <div className="flex justify-center">
                <button 
                  onClick={handleBookingClick}
                  className="bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] hover:from-[#ECD05B] hover:via-[#AE7D2F] hover:to-[#ECD05B] text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  Book Your First Visit
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
          {/* Overview Section */}
          <section className="mb-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Your First Visit
                  <div className="w-[200px] h-[3px] bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] mt-4"></div>
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Your first visit is your opportunity to get to know our
                  practice, see our facility, and meet our office team. During
                  your initial appointment, we&#39;ll discuss your dental
                  concerns, collect the relevant health history and background
                  information, and perform a clinical exam to establish a
                  baseline for care.
                </p>
                <button 
                  onClick={handleBookingClick}
                  className="flex items-center gap-3 text-[#AE7D2F] font-semibold hover:text-[#ECD05B] transition-colors duration-300 cursor-pointer"
                >
                  <Calendar className="w-5 h-5" />
                  <span>Schedule Your Consultation Today</span>
                </button>
              </div>
              <div className="relative">
                <Image
                  src="/images/custom_modules_basilisk_split_images_bg.webp"
                  alt="Dr. Iosif Khaimov in dental office"
                  width={600}
                  height={400}
                  className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-lg shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] p-3 rounded-full">
                      <Heart className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">15+ Years</div>
                      <div className="text-gray-600 text-sm">Experience</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* What to Expect Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What to Expect at Your First Visit
              </h2>
              <div className="w-[200px] h-[3px] bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] mx-auto mb-6"></div>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Whether your first appointment is for a dental checkup, a
                consultation, second opinion, or emergency care, we do all we
                can to make your visit a welcoming, informative, and productive
                experience.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: FileText,
                  title: "Health History Review",
                  description:
                    "We'll discuss your dental concerns and medical history to understand your unique needs.",
                },
                {
                  icon: Shield,
                  title: "Comprehensive Exam",
                  description:
                    "Our thorough clinical examination establishes a baseline for your ongoing care.",
                },
                {
                  icon: Heart,
                  title: "Personalized Treatment Plan",
                  description:
                    "We'll explain your options and create a customized plan tailored to your goals.",
                },
              ].map((step, index) => (
                <div
                  key={index}
                  className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] p-4 rounded-full w-fit mb-6">
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Mission Section */}
          <section className="mb-20">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12 text-white">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Our Mission
                </h2>
                <div className="w-[200px] h-[3px] bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] mx-auto mb-8"></div>
                <p className="text-xl leading-relaxed mb-8">
                  We&#39;re dedicated to helping our patients maintain optimal
                  oral health and achieve their cosmetic smile goals. We tailor
                  each treatment plan to address individual needs and fulfill
                  expectations of care.
                </p>
                <p className="text-lg opacity-90">
                  You can count on every member of our friendly and experienced
                  office team to pay close attention to your comfort and address
                  your needs with kindness, patience, and compassion.
                </p>
              </div>
            </div>
          </section>

          {/* Contact CTA Section */}
          <section className="bg-gray-50 rounded-3xl p-8 md:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Ready to Get Started?
                </h2>
                <p className="text-lg text-gray-700 mb-8">
                  Contact our office to schedule your first appointment. We look
                  forward to welcoming you to the Smile4Me family and helping
                  you achieve your best smile.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button className="bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] hover:from-[#ECD05B] hover:via-[#AE7D2F] hover:to-[#ECD05B] text-white px-8 py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2">
                    <Phone className="w-5 h-5" />
                    Call (718) 400-4400
                  </button>
                  <button 
                    onClick={handleBookingClick}
                    className="border-2 border-[#AE7D2F] text-[#AE7D2F] hover:bg-[#AE7D2F] hover:text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Calendar className="w-5 h-5" />
                    Book Online
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="w-6 h-6 text-[#AE7D2F]" />
                    <h3 className="font-semibold text-gray-900">
                      Office Hours
                    </h3>
                  </div>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex justify-between">
                      <span>Mon-Thu:</span>
                      <span>10:00 AM - 7:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Friday:</span>
                      <span>10:00 AM - 2:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday:</span>
                      <span>11:00 AM - 5:00 PM</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <MapPin className="w-6 h-6 text-[#AE7D2F]" />
                    <h3 className="font-semibold text-gray-900">Location</h3>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    31-57 Steinway St.
                    <br />
                    Queens, NY 11103
                  </p>
                  <button 
                    onClick={() => window.open('https://maps.google.com/?q=31-57+Steinway+St,+Queens,+NY+11103', '_blank', 'noopener,noreferrer')}
                    className="text-[#AE7D2F] hover:text-[#ECD05B] font-semibold text-sm mt-3 transition-colors duration-300"
                  >
                    Get Directions →
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Trust Indicators */}
          <section className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Why Patients Choose Smile4Me
              </h2>
              <div className="w-[150px] h-[3px] bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] mx-auto"></div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { number: "15+", label: "Years Experience" },
                { number: "5000+", label: "Happy Patients" },
                { number: "★★★★★", label: "5-Star Reviews" },
                { number: "24/7", label: "Emergency Care" },
              ].map((stat, index) => (
                <div key={index} className="p-6">
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default NewPatientsPage;